import { currentPostedDate } from '@fruitful/fruitils';
import { subDays } from 'date-fns';
import { router } from 'expo-router';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import {
    ArrowRightIcon,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    LabelRegular,
    type RowHeaderProps,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import {
    DEFAULT_DATE_FORMAT,
    format,
} from '../../../../../common/utils/dateUtils';
import { captureException } from '../../../../../logger/Logger';
import { useGetPaymentTransactionTable } from '../../../../hooks/transaction/useGetPaymentTransactionTable';
import useGetTransactionTable from '../../../../hooks/transaction/useGetTransactionTable';
import {
    AccountTypeFilter,
    type Transaction,
} from '../../../../types/Transactions';
import { EmptyTransactions } from './EmptyTransactions';
import TransactionList from './TransactionList';

const TRANSACTIONS_PER_PAGE = 10;

interface SingleTableViewProps {
    transactions: Transaction[];
    syncteraAccountId: string;
    isPaymentTransactionTable: boolean;
    bankAccountId: string;
}

const SingleTableView = ({
    transactions,
    syncteraAccountId,
    isPaymentTransactionTable,
    bankAccountId,
}: SingleTableViewProps) => {
    const headerProps: RowHeaderProps = {
        title: isPaymentTransactionTable ? 'Payment history' : 'Transactions',
        rightContent: (
            <XStack
                alignItems="center"
                gap={4}
                onPress={() => {
                    isPaymentTransactionTable
                        ? router.navigate({
                              pathname: '/banking/card/payment-history',
                              params: {
                                  syncteraAccountId,
                              },
                          })
                        : router.navigate({
                              pathname: '/banking/transactions',
                              params: {
                                  bankAccountId,
                                  syncteraAccountId,
                              },
                          });
                }}
                cursor="pointer"
            >
                <LabelRegular color="$gray1000">View all</LabelRegular>
                <Icon icon={ArrowRightIcon} size={14} color="$gray1000" />
            </XStack>
        ),
    };

    return (
        <TransactionList
            transactions={transactions}
            syncteraAccountId={syncteraAccountId}
            headerProps={headerProps}
        />
    );
};

interface SeparateTableViewsProps {
    pendingPaymentTransactions: Transaction[];
    pendingChargeSecuredAccountTransactions: Transaction[];
    pendingDepositoryAccountTransactions: Transaction[];
    postedTransactions: Transaction[];
    syncteraAccountId: string;
}

const SeparateTableViews = ({
    pendingPaymentTransactions,
    pendingChargeSecuredAccountTransactions,
    pendingDepositoryAccountTransactions,
    postedTransactions,
    syncteraAccountId,
}: SeparateTableViewsProps) => (
    <>
        {!isEmpty(pendingPaymentTransactions) && (
            <TransactionList
                transactions={pendingPaymentTransactions}
                syncteraAccountId={syncteraAccountId}
                headerProps={{ title: 'Pending payment' }}
            />
        )}
        {!isEmpty(pendingChargeSecuredAccountTransactions) && (
            <TransactionList
                transactions={pendingChargeSecuredAccountTransactions}
                syncteraAccountId={syncteraAccountId}
                headerProps={{ title: 'Pending card' }}
            />
        )}
        {!isEmpty(pendingDepositoryAccountTransactions) && (
            <TransactionList
                transactions={pendingDepositoryAccountTransactions}
                syncteraAccountId={syncteraAccountId}
                headerProps={{ title: 'Pending cash' }}
            />
        )}
        {!isEmpty(postedTransactions) && (
            <TransactionList
                transactions={postedTransactions}
                syncteraAccountId={syncteraAccountId}
                headerProps={{ title: 'Posted' }}
            />
        )}
    </>
);

interface TransactionTableProps {
    bankAccountId: string;
    syncteraAccountId: string;
    allowPagination?: boolean;
    fromDate?: Date;
    toDate?: Date;
    type?: AccountTypeFilter | null;
    forceRefetch?: boolean;
    isPaymentTransactionTable?: boolean;
    isSingleTableView?: boolean;
}

const TransactionTable = ({
    bankAccountId,
    syncteraAccountId,
    allowPagination = false,
    fromDate,
    toDate,
    type,
    forceRefetch,
    isPaymentTransactionTable = false,
    isSingleTableView = false,
}: TransactionTableProps) => {
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [postedPageToken, setPostedPageToken] = useState<string>('');
    const [paginationFilterKey, setPaginationFilterKey] = useState('');
    const [
        pendingChargeSecuredAccountTransactions,
        setPendingChargeSecuredAccountTransactions,
    ] = useState<Transaction[]>([]);
    const [pendingPaymentTransactions, setPendingPaymentTransactions] =
        useState<Transaction[]>([]);
    const [
        pendingDepositoryAccountTransactions,
        setPendingDepositoryAccountTransactions,
    ] = useState<Transaction[]>([]);
    const [postedTransactions, setPostedTransactions] = useState<Transaction[]>(
        [],
    );
    const [nextPostedPageToken, setNextPostedPageToken] = useState<
        string | null
    >(null);
    const [transactionsLoaded, setTransactionsLoaded] = useState(false);

    const currentToDate = format(
        toDate ?? currentPostedDate(),
        DEFAULT_DATE_FORMAT,
    );
    const currentFromDate = format(
        fromDate ?? subDays(toDate ?? currentPostedDate(), 120),
        DEFAULT_DATE_FORMAT,
    );
    const filterKey = `${currentFromDate}|${currentToDate}|${type ?? AccountTypeFilter.ALL}`;
    // if filters have changed since postedPageToken was written, ignore postedPageToken value
    // otherwise, we would query with updated filters but a stale page token
    const postedPageTokenForQuery =
        paginationFilterKey === filterKey ? postedPageToken : '';

    const paymentTransactionTableResult = useGetPaymentTransactionTable(
        {
            syncteraAccountId,
            limit: TRANSACTIONS_PER_PAGE,
            fromDate: currentFromDate,
            toDate: currentToDate,
            ...(postedPageTokenForQuery
                ? { postedPageToken: postedPageTokenForQuery }
                : {}),
        },
        {
            enabled: isPaymentTransactionTable,
        },
    );

    const transactionTableResult = useGetTransactionTable(
        {
            bankAccountId,
            limit: TRANSACTIONS_PER_PAGE,
            fromDate: currentFromDate,
            toDate: currentToDate,
            ...(postedPageTokenForQuery
                ? { postedPageToken: postedPageTokenForQuery }
                : {}),
            ...(type != null && type !== AccountTypeFilter.ALL ? { type } : {}),
        },
        { enabled: !isPaymentTransactionTable },
    );

    const { data, isLoading, isFetching, isError, error, refetch } =
        isPaymentTransactionTable
            ? paymentTransactionTableResult
            : transactionTableResult;

    useEffect(() => {
        if (data && !isFetching) {
            const isPaginatedRequest = postedPageTokenForQuery !== '';

            if ('postedPaymentTransactions' in data) {
                setPendingPaymentTransactions(data.pendingPaymentTransactions);
                setPostedTransactions((prev) =>
                    isPaginatedRequest
                        ? [...prev, ...data.postedPaymentTransactions]
                        : data.postedPaymentTransactions,
                );
            } else if ('postedTransactions' in data) {
                setPendingChargeSecuredAccountTransactions(
                    data.pendingChargeSecuredAccountTransactions ?? [],
                );
                setPendingDepositoryAccountTransactions(
                    data.pendingDepositoryAccountTransactions ?? [],
                );
                setPostedTransactions((prev) =>
                    isPaginatedRequest
                        ? [...prev, ...data.postedTransactions]
                        : data.postedTransactions,
                );
                setNextPostedPageToken(data.nextPostedPageToken);
            }
            setIsLoadingMore(false);
            setTransactionsLoaded(true);
        }
    }, [data, isFetching, postedPageTokenForQuery]);

    useEffect(() => {
        return () => {
            setPostedTransactions([]);
            setPostedPageToken('');
            setNextPostedPageToken(null);
            setTransactionsLoaded(false);
        };
    }, []);

    useEffect(() => {
        if (isError) {
            captureException(new Error('Error fetching transactions'), {
                bankAccountId,
                syncteraAccountId,
                error,
            });
        }
    }, [isError, error, bankAccountId, syncteraAccountId]);

    const handleLoadMore = useCallback(() => {
        if (
            !isLoadingMore &&
            nextPostedPageToken != null &&
            nextPostedPageToken !== postedPageTokenForQuery
        ) {
            setIsLoadingMore(true);
            setPaginationFilterKey(filterKey);
            setPostedPageToken(nextPostedPageToken);
        }
    }, [
        filterKey,
        isLoadingMore,
        nextPostedPageToken,
        postedPageTokenForQuery,
    ]);

    useEffect(() => {
        if (forceRefetch) {
            void refetch();
        }
    }, [forceRefetch, refetch]);

    useEffect(() => {
        setPendingChargeSecuredAccountTransactions([]);
        setPendingPaymentTransactions([]);
        setPendingDepositoryAccountTransactions([]);
        setPostedTransactions([]);
        setPostedPageToken('');
        setNextPostedPageToken(null);
        setIsLoadingMore(false);
        setTransactionsLoaded(false);
    }, [filterKey]);

    // Combine all transactions in order: pending payment, pending card, pending cash, posted
    const allTransactions = useMemo(
        () => [
            ...(pendingPaymentTransactions ?? []),
            ...(pendingChargeSecuredAccountTransactions ?? []),
            ...(pendingDepositoryAccountTransactions ?? []),
            ...(postedTransactions ?? []),
        ],
        [
            pendingPaymentTransactions,
            pendingChargeSecuredAccountTransactions,
            pendingDepositoryAccountTransactions,
            postedTransactions,
        ],
    );

    const hasTransactions =
        !isEmpty(pendingPaymentTransactions) ||
        !isEmpty(pendingChargeSecuredAccountTransactions) ||
        !isEmpty(pendingDepositoryAccountTransactions) ||
        !isEmpty(postedTransactions);

    const dataLoading = isLoading || isFetching || isLoadingMore;

    if (dataLoading && !hasTransactions) {
        return (
            <YStack>
                <Spinner />
            </YStack>
        );
    }

    if (isError) {
        return (
            <YStack>
                <Animated.View entering={FadeIn.duration(700)}>
                    <BodySmallRegular marginTop={12}>
                        Unable to fetch transactions.
                    </BodySmallRegular>
                </Animated.View>
            </YStack>
        );
    }

    if (transactionsLoaded && !hasTransactions) {
        return (
            <YStack>
                <EmptyTransactions
                    isPaymentTransactionTable={isPaymentTransactionTable}
                />
            </YStack>
        );
    }

    return (
        <YStack gap={16}>
            {isSingleTableView ? (
                <SingleTableView
                    transactions={allTransactions}
                    syncteraAccountId={syncteraAccountId}
                    isPaymentTransactionTable={isPaymentTransactionTable}
                    bankAccountId={bankAccountId}
                />
            ) : (
                <SeparateTableViews
                    pendingPaymentTransactions={pendingPaymentTransactions}
                    pendingChargeSecuredAccountTransactions={
                        pendingChargeSecuredAccountTransactions
                    }
                    pendingDepositoryAccountTransactions={
                        pendingDepositoryAccountTransactions
                    }
                    postedTransactions={postedTransactions}
                    syncteraAccountId={syncteraAccountId}
                />
            )}
            {allowPagination &&
                !isEmpty(nextPostedPageToken) &&
                !isEmpty(postedTransactions) && (
                    <Button
                        text={isLoadingMore ? 'Loading...' : 'Load more'}
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        tone="neutral"
                        onPress={handleLoadMore}
                        buttonProps={{
                            width: 'auto',
                        }}
                    />
                )}
        </YStack>
    );
};

export default TransactionTable;
